import { useDispatch, useSelector } from "react-redux";
import SortIcon from "../../../assets/images/sorting.svg";
import styled from "styled-components";
import { setCurrentPage, updateSort } from "../../../slice/Matterslice";
import dayjs from "dayjs";
import { Tooltip } from "antd";
import { HighlightText } from "../../../utils/HighlightedText";
import { Box } from "@mui/material";
import tokens from "../../../theme/tokens";
import StatusChip from "../../StatusChip/StatusChip";
import MatterActivityCell from "../../MatterActivityCell/MatterActivityCell";
import { resolveEntityName } from "../../../utils/matterFields";

const SortIconColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-right: 1px solid ${tokens.border.default};

  .sort-icon {
    border: none;
    cursor: pointer;
    background: transparent;
    padding: 0;
  }
`;

const Header = styled.div`
  border-right: 1px solid ${tokens.border.default};
`;

const PrimaryText = styled.div`
  max-width: 320px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${tokens.text.primary};
  font-weight: ${tokens.typography.weight.medium};
  font-size: 14px;
  line-height: 18px;
`;

const StackedCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const PrimaryLine = styled.span`
  color: ${tokens.text.primary};
  font-size: 13px;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SecondaryLine = styled.span`
  color: ${tokens.text.muted};
  font-size: 11px;
  line-height: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MutedCell = styled.span`
  color: ${tokens.text.disabled};
  font-size: 13px;
`;

// Shared asc/desc toggle used by both the redux-backed Matters page and
// callers that manage sort state locally (Home → Case List tab).
const nextSortState = (sort, item, clicked) => {
  if (item === clicked) {
    if (sort === "asc") return { sort: "desc", item: clicked };
    if (sort === "desc") return { sort: "asc", item: clicked };
    return { sort: "asc", item: "display_name" };
  }
  return { sort: "asc", item: clicked };
};

// Without options the columns read/write the Matters redux slice, as the
// Matters page always has. Pass { sortState, onSortChange, searchTerm } to
// drive the same columns from local component state instead (Case List tab
// on Home), so clicking sort there doesn't disturb the Matters page.
export const MatterTableColumn = (options = {}) => {
  const { sortState, onSortChange, searchTerm } = options;
  const dispatch = useDispatch();
  const reduxMatter = useSelector((state) => state.matter);

  const sort = sortState ? sortState.sort : reduxMatter.sort;
  const item = sortState ? sortState.item : reduxMatter.item;
  const search = searchTerm !== undefined ? searchTerm : reduxMatter.search;

  const onSort = (e) => {
    const next = nextSortState(sort, item, e);
    if (onSortChange) {
      onSortChange(next.sort, next.item);
      return;
    }
    dispatch(updateSort(next));
    dispatch(setCurrentPage(1));
  };

  return [
    {
      title: (
        <SortIconColumn>
          <span>{"MATTER"}</span>
          <Box
            component="button"
            type="button"
            tabIndex={0}
            onClick={() => onSort("display_name")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                onSort("display_name");
              }
            }}
            className="sort-icon"
          >
            <img src={SortIcon} alt="Sorting Icon" />
          </Box>
        </SortIconColumn>
      ),
      dataIndex: "display_name",
      key: "display_name",
      render: (text) => (
        <Tooltip placement="rightTop" title={text}>
          <PrimaryText>{HighlightText(text, search)}</PrimaryText>
        </Tooltip>
      ),
    },
    {
      title: <Header>ACTIVITY</Header>,
      dataIndex: "activity",
      key: "activity",
      render: (_text, record) => {
        const newCount =
          record?.new_since_last_open ?? record?.documents_new_count ?? 0;
        const reviewCount = record?.documents_review_count ?? 0;
        const processingCount = record?.documents_processing_count ?? 0;
        const docsTotal =
          typeof record?.documents_total === "number"
            ? record.documents_total
            : undefined;
        const lastTouched = record?.last_activity_label || null;

        return (
          <MatterActivityCell
            newCount={newCount}
            reviewCount={reviewCount}
            processingCount={processingCount}
            docsTotal={docsTotal}
            lastTouched={lastTouched}
          />
        );
      },
    },
    {
      title: (
        <SortIconColumn>
          <span>{"PRACTICE / TYPE"}</span>
          <Box
            component="button"
            type="button"
            tabIndex={0}
            onClick={() => onSort("practice_area")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                onSort("practice_area");
              }
            }}
            className="sort-icon"
          >
            <img src={SortIcon} alt="Sorting Icon" />
          </Box>
        </SortIconColumn>
      ),
      dataIndex: "practice_area",
      key: "practice_area",
      render: (practice, record) => {
        const caseType = resolveEntityName(record?.case_type);
        if (!practice && !caseType) {
          return <MutedCell>—</MutedCell>;
        }
        return (
          <StackedCell>
            {practice ? (
              <PrimaryLine>{practice}</PrimaryLine>
            ) : (
              <PrimaryLine>—</PrimaryLine>
            )}
            {caseType && (
              <SecondaryLine>{HighlightText(caseType, search)}</SecondaryLine>
            )}
          </StackedCell>
        );
      },
    },
    {
      title: <Header>TEAM</Header>,
      dataIndex: "principal_attourney",
      key: "principal_attourney",
      render: (attorneyRaw, record) => {
        const attorney = resolveEntityName(attorneyRaw);
        const manager = resolveEntityName(record?.case_manager);
        if (!attorney && !manager) {
          return <MutedCell>—</MutedCell>;
        }
        return (
          <StackedCell>
            <PrimaryLine>
              {attorney ? HighlightText(attorney, search) : "—"}
            </PrimaryLine>
            <SecondaryLine>
              {manager ? HighlightText(manager, search) : "—"}
            </SecondaryLine>
          </StackedCell>
        );
      },
    },
    {
      title: (
        <SortIconColumn>
          <span>{"CREATED"}</span>
          <Box
            component="button"
            type="button"
            tabIndex={0}
            onClick={() => onSort("created_on")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                onSort("created_on");
              }
            }}
            className="sort-icon"
          >
            <img src={SortIcon} alt="Sorting Icon" />
          </Box>
        </SortIconColumn>
      ),
      dataIndex: "created_on",
      key: "created_on",
      render: (text) =>
        text ? (
          <SecondaryLine style={{ fontSize: 12 }}>
            {dayjs(text).format("DD MMM YYYY")}
          </SecondaryLine>
        ) : (
          <MutedCell>—</MutedCell>
        ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (text) => {
        if (!text) return <MutedCell>—</MutedCell>;
        const variant = text === "Open" ? "open" : "closed";
        return <StatusChip variant={variant} size="sm" />;
      },
    },
  ];
};
